import React, { useEffect } from 'react';
import { ArrowLeft, ExternalLink, Mail, MessageCircle } from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';
import { Instagram, Linkedin, Youtube } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { contactInfo, personalInfo, socialLinks } from '../data/mock';
import { trackCtaClick } from '../utils/analytics';
import { applyPageMeta } from '../utils/seo';
import Header from './Header';
import Footer from './Footer';

const LinksPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    applyPageMeta({
      title: 'Links oficiais | Tiago Cardoso',
      description: 'Acesse os links oficiais de Tiago Cardoso, Vivo Creator: Instagram, LinkedIn, YouTube, TikTok e contato profissional.',
      canonical: 'https://ticardoso.com.br/links'
    });

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Redes sociais oficiais de Tiago Cardoso",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "LinkedIn",
          "url": socialLinks.linkedin
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Instagram",
          "url": socialLinks.instagram
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "YouTube",
          "url": socialLinks.youtube
        },
        {
          "@type": "ListItem",
          "position": 4,
          "name": "TikTok",
          "url": socialLinks.tiktok
        }
      ]
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const whatsappUrl = `https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(contactInfo.whatsappMessage)}`;
  const emailUrl = `mailto:${contactInfo.email}`;

  const linkItems = [
    {
      name: 'WhatsApp',
      description: 'Agendar uma conversa sobre projetos de tecnologia.',
      url: whatsappUrl,
      icon: MessageCircle,
      cta: 'whatsapp'
    },
    {
      name: 'LinkedIn',
      description: 'Experiências profissionais, cases e artigos.',
      url: socialLinks.linkedin,
      icon: Linkedin,
      cta: 'linkedin'
    },
    {
      name: 'Instagram',
      description: 'Bastidores de tecnologia, automação e conectividade.',
      url: socialLinks.instagram,
      icon: Instagram,
      cta: 'instagram'
    },
    {
      name: 'YouTube',
      description: 'Conteúdos em vídeo com dicas práticas.',
      url: socialLinks.youtube,
      icon: Youtube,
      cta: 'youtube'
    },
    {
      name: 'TikTok',
      description: 'Insights rápidos sobre inovação e tecnologia.',
      url: socialLinks.tiktok,
      icon: FaTiktok,
      cta: 'tiktok'
    },
    {
      name: 'Email',
      description: 'Envie uma proposta ou briefing direto.',
      url: emailUrl,
      icon: Mail,
      cta: 'email'
    },
    {
      name: 'Portfólio',
      description: 'Veja casos e soluções aplicadas.',
      url: '/#casos',
      icon: ExternalLink,
      cta: 'portfolio'
    }
  ];

  return (
    <div className="links-page">
      <Header />
      <main className="links-content">
        <section className="section-padding">
          <div className="container-custom">
            <button onClick={() => navigate('/')} className="back-button">
              <ArrowLeft size={20} />
              Voltar para home
            </button>

            <div className="links-hero">
              {personalInfo.profileImage && (
                <img
                  src={personalInfo.profileImage}
                  alt={`Foto de ${personalInfo.name}`}
                  className="links-avatar"
                />
              )}
              <h1 className="links-title">Links oficiais</h1>
              <p className="links-subtitle">
                Siga, acompanhe e entre em contato para projetos de tecnologia, conectividade e automação.
              </p>
            </div>

            <div className="links-grid">
              {linkItems.map((item) => {
                const Icon = item.icon;
                const isExternal = item.url.startsWith('http');
                const linkProps = isExternal
                  ? { target: '_blank', rel: 'noopener noreferrer' }
                  : {};

                return (
                  <a
                    key={item.name}
                    href={item.url}
                    onClick={() => trackCtaClick({ cta: item.cta, location: 'links', url: item.url })}
                    className="links-card"
                    {...linkProps}
                  >
                    <span className="links-icon">
                      <Icon size={24} />
                    </span>
                    <div className="links-info">
                      <h2 className="links-name">{item.name}</h2>
                      <p className="links-description">{item.description}</p>
                    </div>
                    <ExternalLink className="links-arrow" size={18} />
                  </a>
                );
              })}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default LinksPage;
